import { skipToken, useMutation, useQuery } from "@tanstack/react-query";
import { type FormEvent, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";
import { queryClient, trpc } from "../../app/trpc.js";
import { Badge, Button, Input, Notice, PageTitle, SendIcon } from "../../ui/index.js";
import { useCabinet } from "../cabinet/index.js";

/** Сколько показываем «бот отвечает» после отправки, если сервер не сообщает стадию (бот промолчал). */
const WAIT_MS = 90_000;

/**
 * Демо-чат: владелец пишет как клиент и видит ответы бота. Тред создаётся при открытии и по кнопке «Новый диалог».
 * Без активного доступа (нет подписки) бот молчит: вместо тишины показываем уведомление со ссылкой на подписку и закрываем ввод.
 * Пока бот готовит ответ, под сообщением клиента пузырёк со стадией по реальным событиям хода (решение 04.11.2026):
 * «Запрос принят» сразу после отправки, пока воркер не взял ход; потом стадию пишет бот (`bot_progress`):
 * думает, смотрит календарь, проверяет услуги, готовит ответ.
 */
export function DemoChatPage() {
  const { t } = useTranslation();
  const { salonId, base } = useCabinet();
  const [threadId, setThreadId] = useState<string | null>(null);
  const [text, setText] = useState("");
  const listRef = useRef<HTMLDivElement>(null);

  const access = useQuery(trpc.bot.demo.access.queryOptions({ salonId }, { staleTime: 60_000, enabled: Boolean(salonId) }));
  const blocked = access.data?.access === "blocked";
  const start = useMutation(trpc.bot.demo.start.mutationOptions({ onSuccess: (r) => setThreadId(r.threadId) }));
  const messages = useQuery(trpc.bot.demo.messages.queryOptions(threadId ? { salonId, threadId } : skipToken, { refetchInterval: 1500 }));
  // Окно ожидания ответа: отсчёт от момента, когда сервер принял сообщение (по часам этого устройства, не сервера).
  const [waitUntil, setWaitUntil] = useState<number | null>(null);
  const send = useMutation(
    trpc.bot.demo.send.mutationOptions({
      onSuccess: () => {
        setWaitUntil(Date.now() + WAIT_MS);
        if (threadId) void queryClient.invalidateQueries(trpc.bot.demo.messages.queryFilter({ salonId, threadId }));
      },
    }),
  );
  // Окно кончилось: убрать пузырёк, даже если опрос приносит те же данные и страница сама не перерисуется.
  useEffect(() => {
    if (waitUntil === null) return;
    const id = setTimeout(() => setWaitUntil(null), Math.max(0, waitUntil - Date.now()));
    return () => clearTimeout(id);
  }, [waitUntil]);

  // Новый тред при открытии страницы (и при смене салона)
  const startThread = start.mutate;
  useEffect(() => {
    startThread({ salonId });
  }, [startThread, salonId]);

  // Автопрокрутка вниз, когда приходит новое сообщение
  const items = messages.data?.messages ?? [];
  const lastId = items.at(-1)?.id;
  const stage = messages.data?.progress ?? null;
  useEffect(() => {
    if ((lastId || stage || send.isPending) && listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight;
  }, [lastId, stage, send.isPending]);

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    const value = text.trim();
    if (!value || !threadId || send.isPending || blocked) return;
    // Поле очищаем сразу: то, что наберут во время отправки, не пропадёт.
    setText("");
    send.mutate({ salonId, threadId, text: value });
  };
  const onNewChat = () => {
    setThreadId(null);
    setText("");
    setWaitUntil(null);
    send.reset();
    start.mutate({ salonId });
  };

  const state = messages.data?.state;
  const progress = messages.data?.progress ?? null;
  const last = items.at(-1);
  // Ждём ответа бота: только что отправили, или последнее слово за клиентом и диалог у бота.
  const waiting = !blocked && (send.isPending || progress !== null || (waitUntil !== null && state === "bot" && last?.author === "client"));
  return (
    <div className="lg:max-w-3xl">
      <PageTitle>{t("demo.title")}</PageTitle>
      <div className="mb-3 flex items-center justify-between gap-2">
        <p className="text-sm text-ink-2">{t("demo.hint")}</p>
        {state && (
          <Badge tone={state === "escalated" ? "amber" : "slate"} dot>
            {t(`conversations.state.${state}`)}
          </Badge>
        )}
      </div>
      {(start.isError || messages.isError || send.isError || access.isError) && <Notice kind="error">{t("common.error")}</Notice>}
      {blocked && (
        <Notice kind="error">
          {t("demo.blocked")}{" "}
          <Link to={`${base}/billing`} className="font-medium underline">
            {t("demo.blockedAction")}
          </Link>
        </Notice>
      )}
      <div ref={listRef} className="my-3 h-[55dvh] space-y-2 overflow-y-auto rounded-card border border-line bg-page p-3">
        {threadId && items.length === 0 && <div className="py-8 text-center text-sm text-ink-3">{t("demo.empty")}</div>}
        {items.map((m) => {
          if (m.author === "system") {
            return (
              <div key={m.id} className="text-center text-xs text-ink-3">
                {m.text}
              </div>
            );
          }
          const own = m.author === "client";
          const body = m.text;
          return (
            <div key={m.id} className={`flex flex-col ${own ? "items-end" : "items-start"}`}>
              {m.author === "staff" && <div className="mb-0.5 text-xs text-ink-3">{t("demo.staff")}</div>}
              <div
                className={`max-w-[80%] whitespace-pre-wrap break-words rounded-2xl px-3.5 py-2 text-sm ${
                  own ? "rounded-br-md bg-brand text-white" : "rounded-bl-md border border-line bg-white text-ink"
                } ${!own && m.deliveryStatus === "pending" ? "opacity-60" : ""}`}
              >
                {body}
              </div>
              {!own && m.deliveryStatus === "pending" && <div className="mt-0.5 text-xs text-ink-3">{t("demo.sending")}</div>}
              {!own && m.deliveryStatus === "failed" && <div className="mt-0.5 text-xs text-red-600">{t("demo.failed")}</div>}
            </div>
          );
        })}
        {waiting && (
          <div className="flex items-start" aria-live="polite">
            <div className="inline-flex items-center gap-2 rounded-card border border-line bg-white px-3 py-2 text-sm text-ink-3">
              <span className="flex gap-1" aria-hidden="true">
                <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-slate-400 [animation-delay:-0.3s]" />
                <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-slate-400 [animation-delay:-0.15s]" />
                <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-slate-400" />
              </span>
              {t(`demo.progress.${progress ?? "accepted"}`)}
            </div>
          </div>
        )}
      </div>
      <form onSubmit={onSubmit} className="flex gap-2">
        <Input value={text} onChange={(e) => setText(e.target.value)} placeholder={t("demo.placeholder")} maxLength={2000} disabled={!threadId || blocked} />
        <Button type="submit" icon={<SendIcon />} disabled={!threadId || blocked || send.isPending || !text.trim()}>
          {t("demo.send")}
        </Button>
      </form>
      <Button variant="ghost" className="mt-3 w-full lg:w-auto" onClick={onNewChat} disabled={start.isPending}>
        {t("demo.newChat")}
      </Button>
    </div>
  );
}
